/**
 * Commission Import Page
 * Route: /commission/import
 *
 * Provides CSV upload, preview, and confirmation flow for importing
 * commission transactions from lender statement files.
 *
 * States: Empty → Validating → Preview → Complete
 */

import { useState, useRef, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { Upload, FileText, CheckCircle2, AlertCircle, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { getAuthHeaders } from "@/lib/queryClient";
import { Link } from "wouter";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PreviewRow {
  row: number;
  status: "valid" | "error" | "duplicate";
  data: {
    transactionDate: string;
    lenderName: string;
    accountNumber: string;
    commissionType: string;
    commissionAmount: string;
    totalAmount: string;
  };
  field?: string;
  message?: string;
}

interface ImportPreviewResponse {
  batchId: string;
  totalRows: number;
  validRows: number;
  duplicateRows: number;
  errorRows: number;
  errors: Array<{ row: number; field: string; message: string }>;
  /**
   * Non-blocking. A column redacted as a suspected TFN still imports, but the stored value
   * will not match the source file. Optional so an older API response still parses.
   */
  warnings?: Array<{ row: number; field: string; message: string }>;
  preview: PreviewRow[];
}

interface ImportConfirmResponse {
  imported: number;
  skipped: number;
  batchId: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ACCEPTED_MIME_TYPES = new Set([
  "text/csv",
  "text/plain",
  "application/octet-stream",
  "application/vnd.ms-excel",
]);

const SUPPORTED_COLUMNS = [
  "transaction_date",
  "lender_name",
  "account_number",
  "commission_type",
  "commission_amount",
  "gst_amount",
  "total_amount",
  "client_name (optional)",
  "transaction_reference (optional)",
  "payment_date (optional)",
];

// ---------------------------------------------------------------------------
// Row status badge
// ---------------------------------------------------------------------------

interface RowStatusBadgeProps {
  status: "valid" | "error" | "duplicate";
  message?: string;
}

function RowStatusBadge({ status, message }: RowStatusBadgeProps) {
  if (status === "valid") {
    return (
      <span data-testid="row-status-valid" className="flex items-center gap-1 text-green-700">
        <CheckCircle2 className="h-4 w-4" />
        <span className="text-xs">Valid</span>
      </span>
    );
  }
  if (status === "duplicate") {
    return (
      <span data-testid="row-status-duplicate" className="flex items-center gap-1 text-yellow-700">
        <AlertTriangle className="h-4 w-4" />
        <span className="text-xs">Duplicate</span>
      </span>
    );
  }
  return (
    <span
      data-testid="row-status-error"
      className="flex items-center gap-1 text-red-700"
      title={message}
    >
      <AlertCircle className="h-4 w-4" />
      <span className="text-xs">Error</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function CommissionImportPageContent() {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ImportPreviewResponse | null>(null);
  const [importDone, setImportDone] = useState<ImportConfirmResponse | null>(null);

  // -------------------------------------------------------------------------
  // Mutations
  // -------------------------------------------------------------------------

  const uploadMutation = useMutation<ImportPreviewResponse, Error, File>({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);

      // Include auth headers only — NOT Content-Type (browser auto-sets multipart boundary)
      // getAuthHeaders returns {} if no Supabase session; Playwright injects Authorization in tests
      const authHeaders = await getAuthHeaders();
      const response = await fetch("/api/commissions/import", {
        method: "POST",
        headers: authHeaders,
        credentials: "include",
        body: formData,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: "Upload failed" }));
        throw new Error(err.error ?? "Upload failed");
      }

      return response.json();
    },
    onSuccess: (data) => {
      setPreview(data);
      setFileError(null);
    },
    onError: (err) => {
      setFileError(err.message);
      setPreview(null);
    },
  });

  const confirmMutation = useMutation<ImportConfirmResponse, Error, string>({
    mutationFn: async (batchId: string) => {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(`/api/commissions/import/${batchId}/confirm`, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: "Confirm failed" }));
        throw new Error(err.error ?? "Confirm failed");
      }

      return response.json();
    },
    onSuccess: (data) => {
      setImportDone(data);
      setPreview(null);
      toast({
        title: "Import complete",
        description: `Imported ${data.imported} commission transaction${data.imported !== 1 ? "s" : ""}`,
      });
    },
    onError: (err) => {
      toast({
        title: "Import failed",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  // -------------------------------------------------------------------------
  // File handling
  // -------------------------------------------------------------------------

  const handleFile = useCallback(
    (file: File) => {
      setFileError(null);
      setPreview(null);
      setImportDone(null);

      // Validate file type — accept .csv by name or MIME
      const isCsvByName = file.name.toLowerCase().endsWith(".csv");
      const isCsvByMime = ACCEPTED_MIME_TYPES.has(file.type) || file.type === "";

      if (!isCsvByName && !isCsvByMime) {
        setFileError(`Invalid file type: ${file.name}. Only CSV files are accepted.`);
        return;
      }

      uploadMutation.mutate(file);
    },
    [uploadMutation]
  );

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // Reset so same file can be selected again
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const resetToEmpty = () => {
    setPreview(null);
    setImportDone(null);
    setFileError(null);
    uploadMutation.reset();
    confirmMutation.reset();
  };

  // -------------------------------------------------------------------------
  // Render: Complete state
  // -------------------------------------------------------------------------

  if (importDone) {
    return (
      <div className="mx-auto max-w-3xl space-y-6">
        <Card className="p-8 text-center space-y-4">
          <CheckCircle2 className="mx-auto h-12 w-12 text-green-500" />
          <div>
            <p className="text-lg font-semibold">Import Complete</p>
            <p className="text-muted-foreground">
              {importDone.imported} transaction{importDone.imported !== 1 ? "s" : ""} imported
              {importDone.skipped > 0 && `, ${importDone.skipped} skipped`}
            </p>
          </div>
          <div className="flex justify-center gap-3">
            <Link href="/commission/transactions">
              <Button>View Transactions</Button>
            </Link>
            <Button variant="outline" onClick={resetToEmpty}>
              Import Another File
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Render: Preview state
  // -------------------------------------------------------------------------

  if (preview) {
    const { batchId, totalRows, validRows, duplicateRows, errorRows } = preview;
    const redactionWarnings = preview.warnings ?? [];

    return (
      <div className="mx-auto max-w-5xl space-y-4">
        {/* Summary */}
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div data-testid="import-summary" className="text-sm space-x-4">
              <span className="font-medium">{totalRows} rows parsed</span>
              <span className="text-green-700">
                <CheckCircle2 className="inline h-3.5 w-3.5 mr-1" />
                {validRows} valid
              </span>
              {duplicateRows > 0 && (
                <span className="text-yellow-700">
                  <AlertTriangle className="inline h-3.5 w-3.5 mr-1" />
                  {duplicateRows} duplicate{duplicateRows !== 1 ? "s" : ""} skipped
                </span>
              )}
              {errorRows > 0 && (
                <span className="text-red-700">
                  <AlertCircle className="inline h-3.5 w-3.5 mr-1" />
                  {errorRows} error{errorRows !== 1 ? "s" : ""}
                </span>
              )}
              {redactionWarnings.length > 0 && (
                <span className="text-warning" data-testid="import-warning-count">
                  <AlertTriangle className="inline h-3.5 w-3.5 mr-1" />
                  {redactionWarnings.length} redacted value
                  {redactionWarnings.length !== 1 ? "s" : ""}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={resetToEmpty}
                data-testid="button-cancel-import"
              >
                Cancel
              </Button>
              <Button
                onClick={() => confirmMutation.mutate(batchId)}
                disabled={validRows === 0 || confirmMutation.isPending}
                data-testid="button-import-valid"
              >
                {confirmMutation.isPending
                  ? "Importing..."
                  : `Import ${validRows} Valid Row${validRows !== 1 ? "s" : ""}`}
              </Button>
            </div>
          </div>
        </Card>

        {/*
          Redaction warnings. These rows DO import — the TFN control rewrote a value on the
          way in, so what lands will not match the source file. Naming the row and column is
          the point: without it the change is silent, and a payment reference that no longer
          matches is discovered at reconciliation instead of at import.
        */}
        {redactionWarnings.length > 0 && (
          <Card className="p-4 border-warning/30 bg-warning/10" data-testid="import-warnings">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 text-warning shrink-0" />
              <div className="text-sm space-y-2">
                <p className="font-medium text-warning">
                  {redactionWarnings.length} value
                  {redactionWarnings.length !== 1 ? "s were" : " was"} redacted as a suspected
                  tax file number
                </p>
                <p className="text-foreground">
                  These rows still import, but the stored value will not match your file.
                  Check them against the source before importing.
                </p>
                <ul className="list-disc pl-5 text-foreground">
                  {redactionWarnings.slice(0, 20).map((w, i) => (
                    <li key={`${w.row}-${w.field}-${i}`}>
                      Row {w.row}, column <span className="font-mono">{w.field}</span>
                    </li>
                  ))}
                </ul>
                {redactionWarnings.length > 20 && (
                  <p className="text-muted-foreground">
                    and {redactionWarnings.length - 20} more
                  </p>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Preview table */}
        <Card>
          <Table data-testid="import-preview-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">#</TableHead>
                <TableHead className="w-20">Status</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Lender</TableHead>
                <TableHead>Account #</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {preview.preview.slice(0, 100).map((row) => (
                <TableRow
                  key={row.row}
                  className={
                    row.status === "error"
                      ? "bg-red-50"
                      : row.status === "duplicate"
                      ? "bg-yellow-50"
                      : ""
                  }
                >
                  <TableCell className="text-muted-foreground text-xs">
                    {row.row}
                  </TableCell>
                  <TableCell>
                    <RowStatusBadge status={row.status} message={row.message} />
                  </TableCell>
                  <TableCell className="text-sm">{row.data.transactionDate}</TableCell>
                  <TableCell className="text-sm">{row.data.lenderName}</TableCell>
                  <TableCell className="font-mono text-sm">{row.data.accountNumber}</TableCell>
                  <TableCell className="capitalize text-sm">{row.data.commissionType}</TableCell>
                  <TableCell className="text-right text-sm">{row.data.commissionAmount}</TableCell>
                  <TableCell className="text-right text-sm">{row.data.totalAmount}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {preview.preview.length > 100 && (
            <div className="px-4 py-2 text-xs text-muted-foreground border-t">
              Showing first 100 of {preview.preview.length} rows
            </div>
          )}
        </Card>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Render: Empty / Validating state
  // -------------------------------------------------------------------------

  const isValidating = uploadMutation.isPending;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* Drop zone */}
      <Card
        data-testid="csv-drop-zone"
        className={`p-12 text-center border-2 border-dashed transition-colors cursor-pointer ${
          dragOver
            ? "border-primary bg-primary/5"
            : "border-muted-foreground/30 hover:border-primary/50"
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !isValidating && fileInputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            fileInputRef.current?.click();
          }
        }}
        aria-label="Upload CSV file"
      >
        {isValidating ? (
          <div className="space-y-3">
            <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            <p className="text-lg font-medium">Validating...</p>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                uploadMutation.reset();
              }}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <div className="space-y-3 pointer-events-none">
            <Upload className="mx-auto h-12 w-12 text-muted-foreground" />
            <div>
              <p className="text-lg font-medium">
                Drag &amp; drop CSV file here or click to browse
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Maximum file size: 10 MB
              </p>
            </div>
          </div>
        )}
      </Card>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        onChange={handleFileInputChange}
        className="sr-only"
        data-testid="csv-file-input"
        aria-label="Choose CSV file"
        tabIndex={-1}
      />

      {/* File error */}
      {fileError && (
        <Card
          className="p-4 border-red-200 bg-red-50"
          data-testid="import-file-error"
        >
          <div className="flex items-start gap-3 text-red-800">
            <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">File rejected</p>
              <p className="text-sm mt-0.5">{fileError}</p>
            </div>
          </div>
        </Card>
      )}

      {/* Supported columns info */}
      <Card className="p-4">
        <div className="flex items-start gap-3">
          <FileText className="h-5 w-5 mt-0.5 text-muted-foreground flex-shrink-0" />
          <div>
            <p className="text-sm font-medium mb-2">Supported CSV columns</p>
            <div className="grid grid-cols-2 gap-x-8 gap-y-1">
              {SUPPORTED_COLUMNS.map((col) => (
                <code key={col} className="text-xs text-muted-foreground font-mono">
                  {col}
                </code>
              ))}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
